import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3, LayoutDashboard, AlertTriangle, Clock, ExternalLink, Loader2,
} from 'lucide-react';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit, emitDrill, subscribe } from '../lib/embedChannel';
import EmbedDrillBreadcrumb from '../components/embed/EmbedDrillBreadcrumb';

/**
 * Embed Insights (iframe surface).
 *
 * Mounted at `/embed/v2/insights?token=<jwt>&kind=all|report|dashboard`.
 * The host embeds this page in an iframe. It:
 *   1. Reads the JWT from `?token=`.
 *   2. Calls `POST /api/embed/v2/authenticate` to render viewer identity.
 *   3. Calls `GET  /api/embed/v2/insights?token=...` to load the feed.
 *   4. Posts events to the parent window via `postMessage` so the host
 *      can react to clicks / errors / identify events.
 *
 * No sidebar, no top nav — this is a self-contained embed surface.
 * Everything derives from the token; there is no direct login flow.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authenticate(token) {
  const res = await fetch(`${API}/api/embed/v2/authenticate`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || `authenticate ${res.status}`);
  }
  return res.json();
}

async function fetchInsights(token, kind) {
  const url = new URL(`${API}/api/embed/v2/insights`);
  if (kind && kind !== 'all') url.searchParams.set('kind', kind);
  const res = await fetch(url.toString(), {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || `insights ${res.status}`);
  }
  return res.json();
}

function KindBadge({ kind }) {
  const cls = kind === 'dashboard'
    ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/40'
    : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40';
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded border ${cls}`}>
      {kind === 'dashboard' ? <LayoutDashboard className="w-2.5 h-2.5" /> : <BarChart3 className="w-2.5 h-2.5" />}
      {kind}
    </span>
  );
}

function ErrorPanel({ error }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-background"
      data-testid="embed-insights-error"
    >
      <div className="max-w-md rounded-lg border border-rose-500/40 bg-rose-500/5 p-5 text-center">
        <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto mb-2" />
        <div className="text-sm font-semibold mb-1">Couldn&apos;t load this embed</div>
        <div className="text-xs text-muted-foreground break-words">{error}</div>
        <div className="text-[10px] text-muted-foreground mt-3">
          If this token was minted by your backend, check that it&apos;s not expired,
          revoked, or scoped to a different resource.
        </div>
      </div>
    </div>
  );
}

export default function EmbedInsightsPage() {
  const token = useEmbedToken();
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState('all');
  const [drillCrumb, setDrillCrumb] = useState(null);

  const canWrite = useMemo(
    () => (session?.capabilities || []).includes('write'),
    [session],
  );

  useEffect(() => {
    if (!token) {
      setError('Missing ?token= in URL');
      setLoading(false);
      emit('error', { code: 'missing_token' });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const s = await authenticate(token);
        if (!alive) return;
        setSession(s);
        emit('viewer.identified', {
          workspace_id: s.workspace_id,
          sub: s.sub,
          roles: s.roles,
          capabilities: s.capabilities,
        });
        const feed = await fetchInsights(token, kind);
        if (!alive) return;
        setItems(feed.data || []);
        emit('insights.loaded', { count: feed.data?.length ?? 0, kind });
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
        emit('error', { message: String(e?.message || e) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, kind]);

  // Host → embed command channel: let the parent SDK apply/clear the
  // `kind` filter and re-ping the current session.
  useEffect(() => {
    return subscribe({
      'filter.set': ({ key, value }) => {
        if (key === 'kind' && typeof value === 'string') {
          const allowed = new Set(['all', 'report', 'dashboard']);
          if (allowed.has(value)) {
            setKind((prev) => {
              if (prev === value) return prev;
              emit('filter.applied', { key: 'kind', value, previous: prev });
              return value;
            });
          }
        }
      },
      'filter.clear': ({ key }) => {
        if (!key || key === 'kind') {
          setKind((prev) => {
            if (prev === 'all') return prev;
            emit('filter.cleared', { key: 'kind' });
            return 'all';
          });
        }
      },
      'ping': () => {
        if (session) {
          emit('viewer.identified', {
            workspace_id: session.workspace_id,
            sub: session.sub,
            roles: session.roles,
            capabilities: session.capabilities,
          });
        }
      },
      'drill.response': ({ target, label, ttl_ms }) => {
        if (typeof label !== 'string' || !label.trim()) return;
        setDrillCrumb({ target, label: label.trim(), ttl_ms });
      },
    });
  }, [session]);

  const dismissDrill = useCallback((reason) => {
    setDrillCrumb((prev) => {
      if (prev) emit('drill.dismissed', { target: prev.target, reason });
      return null;
    });
  }, []);

  const openInsight = useCallback((it) => {
    // We deliberately do NOT navigate inside the iframe. The host is the
    // authority — they might want to open a drill-through target in
    // their own UI (e.g. a modal) or route to their own page. Emit the
    // canonical `drill.request` (v0.4+) plus the legacy `insight.opened`
    // event so older SDK versions keep working.
    emitDrill('insight', { kind: it.kind, id: it.id, name: it.name });
  }, []);

  if (error) return <ErrorPanel error={error} />;

  const branding = session?.branding || {};
  const productName = brandingProductName(branding, 'Insights');
  const primary = branding.primary_color;

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      data-testid="embed-insights-page"
      style={brandingRootStyle(branding)}
    >
      <div className="max-w-4xl mx-auto p-5">
        {drillCrumb && (
          <EmbedDrillBreadcrumb
            crumb={drillCrumb}
            onDismiss={dismissDrill}
            primaryColor={primary}
          />
        )}
        {/* Compact viewer identity strip */}
        {session && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/25">
            <div className="flex items-center gap-3 min-w-0">
              {branding.logo_url && (
                <img
                  src={branding.logo_url}
                  alt=""
                  className="h-6 w-auto max-w-[120px] object-contain shrink-0"
                  data-testid="embed-insights-logo"
                />
              )}
              <div className="min-w-0">
                <div
                  className="text-[10px] uppercase tracking-widest text-muted-foreground truncate"
                  data-testid="embed-insights-product-name"
                >
                  {productName}
                </div>
                <div className="text-sm truncate">
                  <span className="font-medium">{session.email || session.sub}</span>
                  <span className="text-muted-foreground ml-2 text-xs">
                    · {(session.roles || []).join(', ') || 'viewer'}
                    {canWrite && <span className="ml-1 text-emerald-400">· author</span>}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {['all', 'report', 'dashboard'].map(k => (
                <button
                  key={k}
                  onClick={() => setKind((prev) => {
                    if (prev === k) return prev;
                    if (k === 'all') emit('filter.cleared', { key: 'kind' });
                    else             emit('filter.applied', { key: 'kind', value: k, previous: prev });
                    return k;
                  })}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                    kind === k
                      ? 'text-white'
                      : 'border-border/40 bg-background/40 text-muted-foreground hover:text-foreground'
                  }`}
                  style={
                    kind === k && primary
                      ? { backgroundColor: primary, borderColor: primary }
                      : undefined
                  }
                  data-testid={`embed-insights-kind-${k}`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-8" data-testid="embed-insights-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-border/40 py-10 px-6 text-center"
            data-testid="embed-insights-empty"
          >
            <BarChart3 className="w-5 h-5 mx-auto text-muted-foreground/80 mb-2" />
            <div className="text-sm font-medium">No insights in this workspace yet</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Insights authored inside this workspace will show up here.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="embed-insights-list">
            {items.map(it => (
              <button
                key={`${it.kind}-${it.id}`}
                onClick={() => openInsight(it)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border/40 bg-background/40 hover:border-primary/40 hover:bg-muted/20 transition-colors"
                data-testid={`embed-insight-item-${it.id}`}
              >
                <KindBadge kind={it.kind} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                    <Clock className="w-2.5 h-2.5" />
                    {it.updated_at
                      ? new Date(it.updated_at).toLocaleDateString()
                      : 'never opened'}
                    {(it.tags || []).length > 0 && (
                      <span>· {it.tags.slice(0, 3).join(', ')}</span>
                    )}
                  </div>
                </div>
                <ExternalLink className="w-3 h-3 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
